import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { paymentsAPI } from '../services/api';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Espèces' },
  { value: 'check', label: 'Chèque' },
  { value: 'transfer', label: 'Virement bancaire' },
  { value: 'card', label: 'Carte bancaire' },
  { value: 'other', label: 'Autre' },
];

function PaymentModal({ open, onClose, invoice, onPaymentAdded }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  
  // New payment form
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'transfer',
    reference: '',
    notes: '',
  });

  useEffect(() => {
    if (open && invoice) {
      loadPayments();
    }
  }, [open, invoice]);

  const loadPayments = async () => {
    if (!invoice) return;
    
    try {
      setLoading(true);
      const response = await paymentsAPI.getByInvoice(invoice.id);
      setPayments(response.data.results || response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      setError('Veuillez entrer un montant valide');
      return;
    }

    try {
      setSubmitting(true);
      await paymentsAPI.create({
        invoice: invoice.id,
        amount: parseFloat(newPayment.amount),
        payment_date: newPayment.payment_date,
        payment_method: newPayment.payment_method,
        reference: newPayment.reference,
        notes: newPayment.notes,
      });
      
      // Reset form and reload
      setNewPayment({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'transfer',
        reference: '',
        notes: '',
      });
      setShowAddForm(false);
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
    } catch (err) {
      console.error('Error adding payment:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'ajout du paiement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmPayment = async (paymentId) => {
    try {
      setActionLoading({ ...actionLoading, [paymentId]: true });
      await paymentsAPI.confirm(paymentId);
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
    } catch (err) {
      console.error('Error confirming payment:', err);
      setError(err.response?.data?.error || 'Erreur lors de la confirmation');
    } finally {
      setActionLoading({ ...actionLoading, [paymentId]: false });
    }
  };

  const handleCancelPayment = async (paymentId) => {
    try {
      setActionLoading({ ...actionLoading, [paymentId]: true });
      await paymentsAPI.cancel(paymentId);
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
    } catch (err) {
      console.error('Error cancelling payment:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'annulation');
    } finally {
      setActionLoading({ ...actionLoading, [paymentId]: false });
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement ?')) return;
    
    try {
      setActionLoading({ ...actionLoading, [paymentId]: true });
      await paymentsAPI.delete(paymentId);
      loadPayments();
      if (onPaymentAdded) onPaymentAdded();
    } catch (err) {
      console.error('Error deleting payment:', err);
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setActionLoading({ ...actionLoading, [paymentId]: false });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + ' DA';
  };

  // Calculate totals
  const confirmedTotal = payments
    .filter(p => p.status === 'confirmed')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);
  
  const pendingTotal = payments
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);
  
  const invoiceTotal = invoice?.total_ttc ? parseFloat(invoice.total_ttc) : 0;
  const remaining = invoiceTotal - confirmedTotal;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6">
            Paiements - {invoice?.document?.document_number || 'Facture'}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Invoice Summary */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary">Total Facture</Typography>
              <Typography variant="h6">{formatCurrency(invoiceTotal)}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary">Payé (confirmé)</Typography>
              <Typography variant="h6" color="success.main">{formatCurrency(confirmedTotal)}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary">En attente</Typography>
              <Typography variant="h6" color="warning.main">{formatCurrency(pendingTotal)}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary">Reste à payer</Typography>
              <Typography variant="h6" color={remaining > 0 ? 'error.main' : 'success.main'}>
                {formatCurrency(remaining)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Payments List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Montant</TableCell>
                  <TableCell>Méthode</TableCell>
                  <TableCell>Référence</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        Aucun paiement enregistré
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.payment_date}</TableCell>
                      <TableCell>
                        <strong>{formatCurrency(payment.amount)}</strong>
                      </TableCell>
                      <TableCell>{payment.payment_method_display}</TableCell>
                      <TableCell>{payment.reference || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={payment.status_display} 
                          size="small" 
                          color={getStatusColor(payment.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {actionLoading[payment.id] ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            {payment.status === 'pending' && (
                              <>
                                <Tooltip title="Confirmer">
                                  <IconButton 
                                    size="small" 
                                    color="success"
                                    onClick={() => handleConfirmPayment(payment.id)}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Annuler">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleCancelPayment(payment.id)}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title="Supprimer">
                              <IconButton 
                                size="small" 
                                color="default"
                                onClick={() => handleDeletePayment(payment.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add Payment Form */}
        {showAddForm ? (
          <Paper sx={{ p: 2, mt: 2 }} variant="outlined">
            <Typography variant="subtitle2" gutterBottom>Nouveau paiement</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Montant (DA)"
                  type="number"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date"
                  type="date"
                  value={newPayment.payment_date}
                  onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Méthode</InputLabel>
                  <Select
                    value={newPayment.payment_method}
                    label="Méthode"
                    onChange={(e) => setNewPayment({ ...newPayment, payment_method: e.target.value })}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Référence (N° chèque, virement...)"
                  value={newPayment.reference}
                  onChange={(e) => setNewPayment({ ...newPayment, reference: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  value={newPayment.notes}
                  onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => setShowAddForm(false)}
                    disabled={submitting}
                  >
                    Annuler
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleAddPayment}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
                  >
                    Ajouter
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowAddForm(true)}
            >
              Ajouter un paiement
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PaymentModal;
